import { useWasteService } from 'domain/shared/waste';
import { APPROVE_WASTE, WASTE_STATUSES } from 'domain/shared/waste';
import { useFieldHelper } from 'framework/ui/field';
import { useShowContext } from 'framework/show';
import { useEffect, useState } from 'react';
import { useDateService } from 'framework/utils';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { AccountInput } from 'domain/shared/ledger';
import { BudgetCategoryDropdown } from 'domain/shared/budget';
import { isBudgetCategoryRequired } from 'domain/shared/budget';
import { Box } from '@mui/material';
import { useNewProjectUrl } from 'domain/shared/newProject';

const ApproveProjectWasteButton = ({
  statusSource,
  id,
  projectId,
}: {
  statusSource: string;
  id: string;
  projectId?: string;
}) => {
  const { approveWaste, getAccounts } = useWasteService();
  const { getValue } = useFieldHelper({ source: statusSource });
  const { setData = () => {} } = useShowContext();
  const [sourceAccountId, setSourceAccountId] = useState<any>();
  const [sourceBudgetCategory, setSourceBudgetCategory] = useState<any>();
  const [targetAccountId, setTargetAccountId] = useState<any>();
  const [targetBudgetCategory, setTargetBudgetCategory] = useState<any>();
  const [currentDate, setCurrentDate] = useState<any>();
  const status = getValue();
  const { getCurrentDateTime, formatDateForAPI } = useDateService();
  const now = formatDateForAPI(getCurrentDateTime());
  const { getFinanceUrl } = useNewProjectUrl();

  useEffect(() => {
    if (!currentDate) {
      setCurrentDate(now);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (status !== WASTE_STATUSES.REGISTERED) {
    return null;
  }

  return (
    <ButtonWithConfirmation
      displayMessage="Do you really want to approve this waste?"
      submitMethod={() =>
        approveWaste(id!, { sourceAccountId, targetAccountId, sourceBudgetCategory, targetBudgetCategory })
      }
      afterSubmit={{
        successMessage: 'The Waste has been successfully approved.',
        errorMessage: "Can't approve waste.",
        redirectUrl: getFinanceUrl(projectId),
        execute: (data: any) => {
          setData(data);
        },
      }}
      permissionToCheck={APPROVE_WASTE}
      variant="contained"
      modalChildren={
        <Box sx={{ pt: 2 }}>
          <AccountInput
            onChangeCallback={(data: any) => setSourceAccountId(data.sourceAccountId)}
            value={sourceAccountId}
            source="sourceAccountId"
            label="Source Account"
            placeholder="Select Source Account"
            loadDataMethod={getAccounts}
            cacheKey="source-accounts"
            isRequired
          />
          <BudgetCategoryDropdown
            disabled={!isBudgetCategoryRequired(sourceAccountId)}
            onChangeCallback={(data: any) => setSourceBudgetCategory(data.sourceBudgetCategory)}
            value={sourceBudgetCategory}
            source="sourceBudgetCategory"
            label="Category"
            dateValue={currentDate}
            accountIdValue={sourceAccountId}
          />
          <AccountInput
            onChangeCallback={(data: any) => setTargetAccountId(data.targetAccountId)}
            value={targetAccountId}
            source="targetAccountId"
            label="Target Account"
            placeholder="Select Target Account"
            loadDataMethod={getAccounts}
            cacheKey="target-accounts"
            isRequired
          />
          <BudgetCategoryDropdown
            disabled={!isBudgetCategoryRequired(targetAccountId)}
            onChangeCallback={(data: any) => setTargetBudgetCategory(data.targetBudgetCategory)}
            value={targetBudgetCategory}
            source="targetBudgetCategory"
            label="Category"
            dateValue={currentDate}
            accountIdValue={targetAccountId}
          />
        </Box>
      }
      confirmBtnProps={{
        disabled:
          !sourceAccountId ||
          !targetAccountId ||
          (isBudgetCategoryRequired(targetAccountId) && !targetBudgetCategory) ||
          (isBudgetCategoryRequired(sourceAccountId) && !sourceBudgetCategory),
      }}
    >
      Approve
    </ButtonWithConfirmation>
  );
};

export default ApproveProjectWasteButton;
